import type { EffectiveSettings, ServerSettingsView } from '@lc/contracts';
import { useSaveSettings, useHasRole, useSettings } from '../api/hooks';
import { PageHeader } from '../app/Shell';
import { Badge, Card, DataTable, JsonDocEditor, QueryView, parseJsonObject, unknownKeys, type Json, type JsonCheck } from '../ui';

type Key = keyof EffectiveSettings;

/** Every setting, in the order the page lists them, with what it does. */
const ROWS: Array<{ key: Key; label: string; hint: string }> = [
  { key: 'welcomeCredits', label: '見面禮章節券', hint: '每位新玩家（每人一次）' },
  { key: 'welcomeCreditsMode', label: '見面禮時間', hint: 'signup = 建立角色時；day3 = 遊戲第 3 日' },
  { key: 'starterGiftsPerIpDay', label: '同一網絡每日即送', hint: '超過的訪客要綁定登入才收到新手禮物；0 = 不限' },
  { key: 'illustratedCreditCost', label: '精繪漫畫價錢', hint: '每本要幾張章節券' },
  { key: 'firstFreeIllustratedPages', label: '免費首本只畫幾頁', hint: '0 = 整本' },
  { key: 'aiDailyBudgetUsd', label: 'AI 每日預算（US$）', hint: '全體玩家合計；超過後 AI 功能改用後備' },
  { key: 'storyReasoningEffort', label: '故事推理深度', hint: 'none / minimal / low / medium / high' },
  { key: 'aiModels', label: '文字模型', hint: 'default，以及 intent / narration / storyboard / chat 各自的模型' },
  { key: 'imageModel', label: '圖像模型', hint: '' },
  { key: 'imageQuality', label: '一般圖像質素', hint: 'low / medium / high' },
  { key: 'imagePageQuality', label: '漫畫頁質素', hint: 'low / medium / high' },
  { key: 'pageMaxAttempts', label: '每頁最多畫幾次', hint: '1–5' },
  { key: 'pageLettering', label: '對白嵌字', hint: 'model = 圖像模型寫字；ours = 我們排字；art = 只畫圖，對白框由我們放' },
  { key: 'pageRedraw', label: '對白框數目不符', hint: 'count = 重畫；never = 保留第一張，多的抹走、少的由我們補（只限 model）' },
  { key: 'sandboxMode', label: 'Stripe 測試付款', hint: 'allow = 所有人；testers = 只限測試帳戶' },
  { key: 'sandboxTesters', label: '測試帳戶', hint: '玩家 user id 列表' },
];
const KNOWN_KEYS = ['revision', ...ROWS.map((r) => r.key)];

function check(text: string): JsonCheck {
  const r = parseJsonObject(text);
  return r.parsed ? { ...r, warnings: unknownKeys(r.parsed, KNOWN_KEYS) } : r;
}

const show = (v: unknown) => (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v));
/** Whether the stored settings set this one (else the environment's value is in force). */
const isStored = (stored: Json, key: Key) => stored[key] !== undefined;

/**
 * Server settings: economy, anti-abuse caps, AI spend and models, payment testers. Applied on every
 * server within about 30 seconds of a save, with no deploy. A field left out uses the environment's
 * value; delete a line to go back to it. Server-only: the app never receives these.
 */
export function ServerSettingsPage() {
  const settings = useSettings();
  const save = useSaveSettings();
  const isOwner = useHasRole('owner');

  return (
    <>
      <PageHeader
        title="伺服器設定"
        description="不用重新部署就能改的伺服器設定：經濟數值、防刷上限、AI 預算和模型、Stripe 測試帳戶。儲存後約 30 秒內所有伺服器生效。沒有填的項目用伺服器環境變數的值；刪走一行即回復預設。只限 owner 儲存，並記錄在審計日誌。"
      />
      <QueryView query={settings} loading="載入設定…">
        {(view) => (
          <JsonDocEditor
            doc={view.stored as Json & { revision: number }}
            label="伺服器設定 JSON"
            rows={22}
            check={check}
            canSave={isOwner}
            cantSaveWhy="需要擁有者角色"
            save={(doc, note) => save.mutateAsync({ config: doc, note }).then((v) => ({ revision: v.stored.revision }))}
            saving={save.isPending}
            saveError={save.isError ? save.error : null}
            savedNote={(rev) => `已儲存為 revision ${rev}，所有伺服器約 30 秒內生效。`}
            aside={() => <InForce view={view} />}
          />
        )}
      </QueryView>
    </>
  );
}

/** What each setting is now, and where that comes from. */
function InForce({ view }: { view: ServerSettingsView }) {
  const stored = view.stored as Json;
  return (
    <Card title="現時生效" sub="已儲存的值；未填的用環境變數">
      <DataTable
        rows={ROWS}
        rowKey={(r) => r.key}
        columns={[
          { key: 'label', header: '設定', render: (r) => <span title={r.hint}>{r.label}</span> },
          { key: 'value', header: '現值', render: (r) => <code>{show(view.effective[r.key])}</code> },
          { key: 'source', header: '來源', render: (r) => (isStored(stored, r.key) ? <Badge tone="warn">後台</Badge> : <Badge>環境</Badge>), width: 70 },
          { key: 'default', header: '環境值', render: (r) => <span className="faint">{show(view.defaults[r.key])}</span> },
        ]}
      />
    </Card>
  );
}
